import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';
import { useAuth } from '../contexts/AuthContext';
import { useAuthConfig } from '../contexts/AuthConfigContext';
import { DateRangeProvider, useDateRange } from '../contexts/DateRangeContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import SuperSearch from '../components/SuperSearch';
import LogoGlyph from '../components/LogoGlyph';
import { GlobeIcon } from '../components/Icons';
import TopRelationshipPairs from '../components/TopRelationshipPairs';
import DateRangeSelector from '../components/DateRangeSelector';
import DashboardGrid from '../components/DashboardGrid';
import OrganizationsSection from '../components/OrganizationsSection';
import DecisionsSection from '../components/DecisionsSection';
import TopPaymentsSection from '../components/TopPaymentsSection';
import TopDirectAssignmentsSection from '../components/TopDirectAssignmentsSection';
import TopBandedDaReceiversSection from '../components/TopBandedDaReceiversSection';
import TopBandedDaGiversSection from '../components/TopBandedDaGiversSection';
import './HomePage.css';

/**
 * Home Page Data Component — uses DateRangeContext.
 *
 * Renders a unified DashboardGrid with two columns:
 *
 *   [Featured: TopRelationshipPairs (full width)]
 *   [Most Active Organizations]    [Notable Recent Decisions]
 *   [Highest Payments]             [Highest Direct Assignments]
 *   [Repeat €30k–€38k Recipients]  [Repeat €30k–€38k Issuers]
 *
 * Each column has uniform appearance: card background, scrollable list,
 * section header with "See All" link.
 */
const DashboardData = () => {
  useDocumentTitle('Home');
  const navigate = useNavigate();
  const { dateRange } = useDateRange();

  return (
    <DashboardGrid columns={2} header={<DateRangeSelector />}>
      {/* Featured — Top Org×Entity Relationship Pairs (spans full width) */}
      <DashboardGrid.Featured>
        <TopRelationshipPairs
          limit={6}
          showDirectAssignmentsToggle={true}
          defaultDirectAssignmentsOnly={true}
          className="data-section"
          collapsible
        />
      </DashboardGrid.Featured>

      {/* Column 1 — Most Active Organizations (infinite scroll) */}
      <OrganizationsSection
        collapsible
      />

      {/* Column 2 — Notable Recent Decisions (infinite scroll) */}
      <DecisionsSection
        collapsible
      />

      {/* Row 2, Column 1 — Highest Payment Decisions */}
      <TopPaymentsSection
        collapsible
      />

      {/* Row 2, Column 2 — Highest Direct-Assignment Decisions */}
      <TopDirectAssignmentsSection
        collapsible
      />

      {/* Row 3, Column 1 — Repeat €30k–€38k Recipients (by frequency) */}
      <TopBandedDaReceiversSection
        collapsible
        directOnly
      />

      {/* Row 3, Column 2 — Repeat €30k–€38k Issuers (by frequency) */}
      <TopBandedDaGiversSection
        collapsible
        directOnly
      />
    </DashboardGrid>
  );
};

/**
 * HomePage Component with Modular Architecture
 */
const HomePage = () => {
  const { t } = useTranslation();
  const { isSignedIn, isLoaded } = useAuth();
  const { stealthMode, loading: configLoading } = useAuthConfig();
  const navigate = useNavigate();

  // Show loading state while checking config
  if (configLoading) {
    return (
      <div className="homepage">
        <div className="homepage-container">
          <div className="homepage-loading">
            <div className="loading-spinner"></div>
            <p>{t('homepage.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show sign-in prompt if stealth mode is enabled and user is not signed in
  if (isLoaded && stealthMode && !isSignedIn) {
    return (
      <div className="homepage">
        <div className="homepage-container">
          {/* Hero Section */}
          <section className="hero-section">
            <div className="hero-content">
              <div className="hero-brand">
                <LogoGlyph size={40} />
                <h1 className="hero-title">
                  {t('homepage.title')}
                </h1>
              </div>
              <p className="hero-subtitle">
                {t('homepage.subtitle')}
              </p>

              {/* Super Search + Browse button */}
              <div className="hero-action-bar">
                <SuperSearch
                  placeholder={t('homepage.searchPlaceholder')}
                  autoFocus={false}
                  showFullResults={true}
                  className="homepage-super-search"
                />
                <button
                  className="homepage-browse-btn"
                  onClick={() => navigate('/browse')}
                  title={t('homepage.browse')}
                >
                  <GlobeIcon size={18} />
                  {t('homepage.browse')}
                </button>
              </div>

              {/* Sign-in message */}
              <div style={{
                padding: '24px',
                backgroundColor: 'var(--card-bg, #ffffff)',
                borderRadius: '12px',
                textAlign: 'center',
                maxWidth: '600px',
                margin: '48px auto 0'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
                <h2 style={{ marginBottom: '12px' }}>
                  {t('homepage.signInRequired') || 'Sign In to Explore'}
                </h2>
                <p style={{ color: 'var(--muted-text, #666666)' }}>
                  {t('homepage.signInMessage') || 'Please sign in to view trending organizations, recent decisions, and more.'}
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <DateRangeProvider defaultPeriod="week">
      <div className="homepage">
        <div className="homepage-container">
          {/* Hero Section */}
          <section className="hero-section">
            <div className="hero-content">
              <div className="hero-brand">
                <LogoGlyph size={40} />
                <h1 className="hero-title">
                  {t('homepage.title')}
                </h1>
              </div>
              <p className="hero-subtitle">
                {t('homepage.subtitle')}
              </p>

              {/* Super Search + Browse button */}
              <div className="hero-action-bar">
                <SuperSearch
                  placeholder={t('homepage.searchPlaceholder')}
                  autoFocus={false}
                  showFullResults={true}
                  className="homepage-super-search"
                />
                <button
                  className="homepage-browse-btn"
                  onClick={() => navigate('/browse')}
                  title={t('homepage.browse')}
                >
                  <GlobeIcon size={18} />
                  {t('homepage.browse')}
                </button>
              </div>
            </div>
          </section>

          {/* Date Range Selector + Dashboard Data — unified card */}
          <DashboardData />
        </div>
      </div>
    </DateRangeProvider>
  );
};

export default HomePage;
